"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import Link from "next/link"
import { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { useScrollSpy } from "@/lib/useScrollSpy"
import { useKeyboardOpen } from "@/lib/useKeyboardOpen"

interface NavItem {
  name: string
  url: string
  icon: LucideIcon
}

interface NavBarProps {
  items: NavItem[]
  className?: string
}

export function NavBar({ items, className }: NavBarProps) {
  // Highlight follows the section in view (scrollspy), not the last click, so the
  // active tab stays correct while the user scrolls between sections.
  const activeId = useScrollSpy(items.map((item) => item.url.replace(/^#/, "")))

  // iOS Safari / in-app browsers pin `position: fixed; bottom: 0` to the LARGE
  // (layout) viewport, so on first load — while the bottom toolbar is showing —
  // the dock hides behind it; it only clears once you scroll and the toolbar
  // collapses. Track the visual viewport and publish the obscured bottom inset
  // (toolbar/keyboard) as a global `--nav-inset` CSS var so this dock — and the
  // BackToTop button, which must stay clear of it — can both lift above the
  // chrome. Mobile only — at lg+ the bar pins to the top, where it's unwanted.
  useEffect(() => {
    const vv = window.visualViewport
    const root = document.documentElement
    if (!vv) return
    const desktop = window.matchMedia("(min-width: 1024px)")
    const update = () => {
      const obscured = desktop.matches
        ? 0
        : Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      root.style.setProperty("--nav-inset", `${obscured}px`)
    }
    update()
    vv.addEventListener("resize", update)
    vv.addEventListener("scroll", update)
    desktop.addEventListener("change", update)
    return () => {
      vv.removeEventListener("resize", update)
      vv.removeEventListener("scroll", update)
      desktop.removeEventListener("change", update)
      root.style.removeProperty("--nav-inset")
    }
  }, [])

  // On mobile the dock is a bottom-center overlay that would sit on top of the
  // scroll-scrubbed hero, so keep it hidden until the user has scrolled past the
  // hero frames section (#hero). At lg+ the bar pins to the top and is always
  // shown (CSS overrides below), so this only gates the mobile bottom dock.
  const [pastHero, setPastHero] = useState(false)
  useEffect(() => {
    const hero = document.getElementById("hero")
    const onScroll = () => {
      if (!hero) {
        setPastHero(true) // no hero on this page → don't hide the dock
        return
      }
      // The pinned hero releases once scrollY passes (its height − one viewport);
      // that's the point the next section appears, so reveal the dock there.
      const threshold = hero.offsetHeight - window.innerHeight
      const past = window.scrollY > threshold
      setPastHero(past)
      // Back in the hero (home) → drop any leftover section hash (#instalacje …)
      // from the URL so it reflects the top of the page again.
      if (!past && window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search)
      }
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])

  // Hide the mobile dock while a soft keyboard is open (e.g. typing name/phone in
  // a form) so it doesn't sit on top of the field. lg+ stays put (no keyboard).
  const keyboardOpen = useKeyboardOpen()
  const showDock = pastHero && !keyboardOpen

  return (
    <div
      className={cn(
        "transition-opacity duration-300",
        // Mobile: fade in only once past the hero, and hide while the keyboard is
        // open. lg: always visible (top pin).
        showDock ? "opacity-100" : "opacity-0 lg:opacity-100",
        // pointer-events-none + lg:bottom-auto: without bottom-auto the desktop
        // pin (lg:top-0) combined with bottom-0 stretches this wrapper to full
        // viewport height, and z-50 makes it eat hovers over everything beneath
        // it. The pill below re-enables pointer events for itself.
        // Below lg the nav is a bottom-center dock, clear of the header; at lg+
        // it pins top-center, where the compact icon pill fits beside the brand
        // and CTA. Text labels only appear at 2xl (see the items below), since
        // the full label row is too wide to coexist with the header otherwise.
        // The bottom margin clears the home-indicator safe area (or a 0.5rem
        // minimum on phones without one) plus the measured browser-chrome inset
        // (--nav-inset) so the dock never tucks behind the iOS toolbar on first
        // paint. max() avoids stacking 1.5rem on top of the inset (too large a
        // gap on notched phones). Neutralised at lg (top pin).
        "pointer-events-none fixed bottom-0 left-1/2 z-50 -translate-x-1/2 mb-[calc(max(0.5rem,env(safe-area-inset-bottom))+var(--nav-inset,0px))] lg:bottom-auto lg:top-0 lg:mb-0 lg:pt-6",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1 bg-surface/80 border border-outline-variant/40 backdrop-blur-xl py-1 px-1 rounded-full",
          // Don't let the faded-out dock capture taps on mobile before it's shown.
          showDock ? "pointer-events-auto" : "pointer-events-none lg:pointer-events-auto",
        )}
      >
        {items.map((item) => {
          const Icon = item.icon
          const isActive = item.url.replace(/^#/, "") === activeId

          return (
            <Link
              key={item.name}
              href={item.url}
              aria-label={item.name}
              aria-current={isActive ? "page" : undefined}
              title={item.name}
              className={cn(
                "relative cursor-pointer whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full transition-colors",
                "text-on-surface/80 hover:text-primary",
                isActive && "bg-primary-container text-primary",
              )}
            >
              <span className="hidden 2xl:inline">{item.name}</span>
              <span className="2xl:hidden">
                <Icon size={18} strokeWidth={2.5} />
              </span>
              {isActive && (
                <motion.div
                  layoutId="lamp"
                  className="absolute inset-0 w-full bg-primary/5 rounded-full -z-10"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                >
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                    <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                    <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-md -top-1" />
                    <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                  </div>
                </motion.div>
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
